import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Package, ArrowLeft, ChartBar, ListChecks, Clock, Buildings, Stack,
  CheckCircle, Warning, ArrowUpRight, FileText, CurrencyInr,
  Plus, Trash, FloppyDisk,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const fmt = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n || 0);
const fmtINR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = (s) => { try { return new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return s || "—"; } };

const TABS = [
  { id: "budget", label: "Budget vs Actual", icon: ChartBar },
  { id: "materials", label: "Materials Required", icon: ListChecks },
  { id: "rate", label: "Rate Analysis", icon: CurrencyInr },
  { id: "stock", label: "Stock Availability", icon: Package },
  { id: "pos", label: "Purchase Orders", icon: ListChecks },
  { id: "receipts", label: "Material Receipts", icon: FileText },
  { id: "issues", label: "Stock Issues", icon: ArrowUpRight },
  { id: "timeline", label: "Progress & Timeline", icon: Clock },
];

const CRUMB_ICON = { block: Buildings, floor: Stack, item: Package };

const PO_STATUS_TINT = {
  open: "bg-[#E8F0FE] text-[#1A73E8] border-[#D2E3FC]",
  partially_fulfilled: "bg-[#FEF7E0] text-[#B06000] border-[#FCE39E]",
  accepted: "bg-[#E6F4EA] text-[#137333] border-[#CEEAD6]",
  rejected: "bg-[#FCE8E6] text-[#C5221F] border-[#F3B4B0]",
  requires_budget_revision: "bg-[#F3E8FD] text-[#7627CD] border-[#E4D0F9]",
};

export default function BOQItemDetail() {
  const { itemId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("budget");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/boq/items/${itemId}/detail`);
        if (alive) setData(res.data);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [itemId]);

  if (loading) return <div className="p-12 text-center text-neutral-400">Loading item detail…</div>;
  if (!data) return <div className="p-12 text-center text-neutral-500">Item not found. <Link to="/boq" className="text-primary underline">Back to BOQ</Link></div>;

  const { item, breadcrumb, budget_vs_actual: bva, linked_pos, timeline,
    stock_availability, material_receipts, stock_issues, issued_qty_total,
    composition, rate_analysis } = data;

  const refetch = async () => {
    try {
      const res = await api.get(`/boq/items/${itemId}/detail`);
      setData(res.data);
    } catch (err) {
      /* refetch failure is non-fatal */
    }
  };

  return (
    <div className="p-8 sm:p-10 max-w-[1400px] mx-auto" data-testid="boq-item-detail">
      {/* Back + Breadcrumb */}
      <button
        onClick={() => nav("/boq")}
        className="text-xs text-neutral-500 hover:text-primary inline-flex items-center gap-1 mb-3"
        data-testid="boq-item-back"
      >
        <ArrowLeft size={14} weight="bold" /> Back to BOQ
      </button>

      <div className="text-[11px] text-neutral-500 flex items-center gap-2 mb-4 flex-wrap">
        {breadcrumb.map((b, i) => {
          const Ic = CRUMB_ICON[b.node_type] || Package;
          return (
            <React.Fragment key={b.id}>
              {i > 0 && <span className="text-neutral-300">/</span>}
              <span className="inline-flex items-center gap-1"><Ic size={11} weight="fill" /> {b.name}</span>
            </React.Fragment>
          );
        })}
        {breadcrumb.length > 0 && <span className="text-neutral-300">/</span>}
        <span className="inline-flex items-center gap-1 text-neutral-800 font-medium">
          <Package size={11} weight="fill" /> {item.name}
        </span>
      </div>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-4xl font-bold tracking-tight flex items-center gap-3">
            <Package size={30} className="text-[#137333]" weight="fill" /> {item.name}
          </h1>
          <div className="flex flex-wrap gap-2 mt-3 text-xs text-neutral-500">
            <span className="px-2 py-0.5 border border-neutral-300 rounded-sm">Category: <span className="text-neutral-900 font-medium">{item.category}</span></span>
            <span className="px-2 py-0.5 border border-neutral-300 rounded-sm">Unit: <span className="text-neutral-900 font-medium">{item.unit || "—"}</span></span>
            {item.description && <span className="px-2 py-0.5 border border-neutral-300 rounded-sm">{item.description}</span>}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">BOQ Budget</div>
          <div className="font-display text-3xl font-bold tabular-nums text-primary">{fmtINR(bva.boq_amount)}</div>
          <div className="text-xs text-neutral-500 tabular-nums">{fmt(bva.boq_qty)} {bva.unit}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-0 border-b border-neutral-200 mb-6" data-testid="boq-item-tabs">
        {TABS.map((t) => {
          const Ic = t.icon;
          const isActive = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 transition-colors -mb-px ${
                isActive
                  ? "border-primary text-primary font-medium"
                  : "border-transparent text-neutral-500 hover:text-neutral-800"
              }`}
              data-testid={`boq-item-tab-${t.id}`}
            >
              <Ic size={15} /> {t.label}
              {t.id === "pos" && <span className="text-[10px] bg-neutral-100 rounded-full px-1.5 py-0.5 tabular-nums">{linked_pos.length}</span>}
              {t.id === "receipts" && <span className="text-[10px] bg-neutral-100 rounded-full px-1.5 py-0.5 tabular-nums">{material_receipts.length}</span>}
              {t.id === "issues" && <span className="text-[10px] bg-neutral-100 rounded-full px-1.5 py-0.5 tabular-nums">{stock_issues.length}</span>}
              {t.id === "timeline" && <span className="text-[10px] bg-neutral-100 rounded-full px-1.5 py-0.5 tabular-nums">{timeline.length}</span>}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {tab === "budget" && <BudgetTab bva={bva} />}
      {tab === "materials" && <MaterialsTab itemId={itemId} composition={composition} totalQty={item.total_qty} unit={item.unit} onSaved={refetch} />}
      {tab === "rate" && <RateAnalysisTab itemId={itemId} composition={composition} rate={rate_analysis} onSaved={refetch} />}
      {tab === "stock" && <StockTab stock={stock_availability} issuedTotal={issued_qty_total} bva={bva} />}
      {tab === "pos" && <POsTab pos={linked_pos} />}
      {tab === "receipts" && <ReceiptsTab receipts={material_receipts} />}
      {tab === "issues" && <IssuesTab issues={stock_issues} unit={bva.unit} total={issued_qty_total} />}
      {tab === "timeline" && <TimelineTab entries={timeline} />}
    </div>
  );
}

/* --- Tab: Budget vs Actual --- */
function BudgetTab({ bva }) {
  const over = bva.over_budget;
  const pct = Math.min(bva.percent_qty || 0, 100);
  const barColor = over ? "bg-[#C5221F]" : pct >= 90 ? "bg-[#B06000]" : "bg-[#137333]";
  return (
    <div className="space-y-6" data-testid="boq-item-tab-content-budget">
      {/* Qty section */}
      <section>
        <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-3">Quantity</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Metric label="BOQ (Budgeted)" value={`${fmt(bva.boq_qty)} ${bva.unit}`} tone="primary" />
          <Metric label="Ordered to date" value={`${fmt(bva.ordered_qty)} ${bva.unit}`} tone="neutral" />
          <Metric label="Pending delivery" value={`${fmt(bva.pending_delivery_qty)} ${bva.unit}`} tone="neutral" />
          <Metric label={over ? "Over budget by" : "Remaining"} value={`${fmt(Math.abs(bva.remaining_qty))} ${bva.unit}`} tone={over ? "danger" : "success"} />
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-neutral-600 mb-1.5">
            <span>Progress</span>
            <span className="tabular-nums">{bva.percent_qty}% ordered</span>
          </div>
          <div className="h-2 bg-neutral-100 rounded-sm overflow-hidden">
            <div className={`h-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
          </div>
          {over && <div className="mt-2 text-[11px] text-[#C5221F] flex items-center gap-1"><Warning size={12} weight="fill" /> This item has exceeded its BOQ quantity.</div>}
        </div>
      </section>

      {/* Amount section */}
      <section>
        <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-3">Amount</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Metric label="BOQ Amount" value={fmtINR(bva.boq_amount)} tone="primary" icon={CurrencyInr} />
          <Metric label="Ordered Amount" value={fmtINR(bva.ordered_amount)} tone="neutral" icon={CurrencyInr} />
          <Metric
            label={bva.remaining_amount < 0 ? "Over Budget by" : "Remaining Budget"}
            value={fmtINR(Math.abs(bva.remaining_amount))}
            tone={bva.remaining_amount < 0 ? "danger" : "success"}
            icon={CurrencyInr}
          />
        </div>
        <div className="mt-3 text-[11px] text-neutral-500">
          {bva.boq_amount > 0 ? `${bva.percent_amount}% of budgeted amount ordered.` : "No BOQ amount set — enter a rate or total amount on this item to see amount progress."}
        </div>
      </section>
    </div>
  );
}

function Metric({ label, value, tone = "neutral", icon: Icon }) {
  const tint = tone === "primary" ? "text-primary"
    : tone === "success" ? "text-[#137333]"
    : tone === "danger" ? "text-[#C5221F]"
    : "text-neutral-900";
  return (
    <div className="border border-neutral-200 bg-white rounded-sm p-3.5">
      <div className="text-[10px] uppercase tracking-wider text-neutral-500 flex items-center gap-1">
        {Icon ? <Icon size={11} /> : null}
        {label}
      </div>
      <div className={`font-display text-2xl font-bold tabular-nums mt-1 ${tint}`}>{value}</div>
    </div>
  );
}

/* --- Tab: Linked POs --- */
function POsTab({ pos }) {
  if (!pos.length) {
    return (
      <div className="border border-dashed border-neutral-200 rounded-sm p-12 text-center" data-testid="boq-item-tab-content-pos">
        <ListChecks size={36} className="mx-auto text-neutral-300 mb-3" />
        <div className="text-sm text-neutral-500">No purchase orders linked to this item yet.</div>
        <div className="text-[11px] text-neutral-400 mt-1">POs are linked automatically when a PO&apos;s category matches this item&apos;s category + unit.</div>
      </div>
    );
  }
  return (
    <div className="border border-neutral-200 bg-white rounded-sm overflow-hidden" data-testid="boq-item-tab-content-pos">
      <table className="w-full text-sm">
        <thead className="bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500">
          <tr>
            <th className="text-left px-3 py-2">PO #</th>
            <th className="text-left px-3 py-2">Vendor</th>
            <th className="text-left px-3 py-2">Date</th>
            <th className="text-right px-3 py-2">Item Qty</th>
            <th className="text-right px-3 py-2">Line Amt</th>
            <th className="text-right px-3 py-2">Total Amt</th>
            <th className="text-left px-3 py-2">Status</th>
            <th className="px-3 py-2 w-8"></th>
          </tr>
        </thead>
        <tbody>
          {pos.map((p) => {
            const tint = PO_STATUS_TINT[p.status] || PO_STATUS_TINT.open;
            return (
              <tr key={p.id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`boq-item-po-${p.id}`}>
                <td className="px-3 py-2 font-medium text-neutral-900">{p.po_number || "—"}</td>
                <td className="px-3 py-2 text-neutral-700">{p.vendor || "—"}</td>
                <td className="px-3 py-2 text-neutral-500 text-xs">{fmtDate(p.po_date)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmt(p.line_qty)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtINR(p.line_amount)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-neutral-500">{fmtINR(p.total_amount || 0)}</td>
                <td className="px-3 py-2"><span className={`text-[10px] px-1.5 py-0.5 border rounded-sm ${tint}`}>{(p.status || "open").replace(/_/g, " ")}</span></td>
                <td className="px-3 py-2 text-right">
                  <Link to={`/pos/${p.id}`} className="text-primary hover:text-[#0039CC]" title="Open PO"><ArrowUpRight size={14} weight="bold" /></Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* --- Tab: Stock Availability --- */
function StockTab({ stock, issuedTotal, bva }) {
  const u = stock?.unit || bva.unit || "";
  return (
    <div className="space-y-4" data-testid="boq-item-tab-content-stock">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric label="In Hand" value={`${fmt(stock?.in_hand || 0)} ${u}`} tone="success" />
        <Metric label="Reserved (Pending Out)" value={`${fmt(stock?.reserved_out || 0)} ${u}`} tone="neutral" />
        <Metric label="Available" value={`${fmt(stock?.available || 0)} ${u}`} tone="primary" />
        <Metric label="Total Issued (Outward)" value={`${fmt(issuedTotal || 0)} ${u}`} tone="neutral" />
      </div>
      {!stock?.found && (
        <div className="text-[11px] text-[#B06000] bg-[#FEF7E0] border border-[#FCE39E] rounded-sm p-3">
          No stock movement recorded for this exact category + unit yet. Numbers will populate as invoices are approved and outward entries are logged.
        </div>
      )}
      <div className="text-[11px] text-neutral-500">
        These numbers come from the Stock-on-Hand ledger for <span className="font-medium text-neutral-800">{bva.unit || "—"}</span>. Cross-project transfers are reflected automatically.
      </div>
    </div>
  );
}

/* --- Tab: Material Receipts --- */
function ReceiptsTab({ receipts }) {
  if (!receipts?.length) {
    return (
      <div className="border border-dashed border-neutral-200 rounded-sm p-12 text-center" data-testid="boq-item-tab-content-receipts">
        <FileText size={36} className="mx-auto text-neutral-300 mb-3" />
        <div className="text-sm text-neutral-500">No approved invoices for this item yet.</div>
        <div className="text-[11px] text-neutral-400 mt-1">Receipts appear once an invoice for a linked PO is approved.</div>
      </div>
    );
  }
  const totalQty = receipts.reduce((s, r) => s + (r.received_qty || 0), 0);
  const totalAmt = receipts.reduce((s, r) => s + (r.received_amount || 0), 0);
  return (
    <div className="space-y-4" data-testid="boq-item-tab-content-receipts">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Metric label="Receipts (count)" value={receipts.length} tone="neutral" />
        <Metric label="Total Received Qty" value={fmt(totalQty)} tone="success" />
        <Metric label="Total Received Value" value={fmtINR(totalAmt)} tone="primary" />
      </div>
      <div className="border border-neutral-200 bg-white rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="text-left px-3 py-2">Invoice #</th>
              <th className="text-left px-3 py-2">Vendor</th>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">PO #</th>
              <th className="text-right px-3 py-2">Qty</th>
              <th className="text-right px-3 py-2">Amount</th>
              <th className="text-left px-3 py-2">Approved</th>
            </tr>
          </thead>
          <tbody>
            {receipts.map((r) => (
              <tr key={r.id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`boq-receipt-${r.id}`}>
                <td className="px-3 py-2 font-medium text-neutral-900">{r.invoice_number || "—"}</td>
                <td className="px-3 py-2 text-neutral-700">{r.vendor || "—"}</td>
                <td className="px-3 py-2 text-neutral-500 text-xs">{r.invoice_date || "—"}</td>
                <td className="px-3 py-2 text-neutral-700 text-xs">{r.po_number || "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmt(r.received_qty)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtINR(r.received_amount)}</td>
                <td className="px-3 py-2 text-xs text-neutral-500">{fmtDate(r.approved_at)}{r.approved_by ? ` · ${r.approved_by}` : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* --- Tab: Stock Issues (Outward) --- */
function IssuesTab({ issues, unit, total }) {
  if (!issues?.length) {
    return (
      <div className="border border-dashed border-neutral-200 rounded-sm p-12 text-center" data-testid="boq-item-tab-content-issues">
        <ArrowUpRight size={36} className="mx-auto text-neutral-300 mb-3" />
        <div className="text-sm text-neutral-500">No outward entries for this item yet.</div>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="boq-item-tab-content-issues">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Metric label="Total Issues" value={issues.length} tone="neutral" />
        <Metric label="Total Qty Issued" value={`${fmt(total)} ${unit}`} tone="primary" />
        <Metric label="Legacy (Unlinked)" value={issues.filter(i => !i.boq_linked).length} tone="neutral" />
      </div>
      <div className="border border-neutral-200 bg-white rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">Flat / Site</th>
              <th className="text-left px-3 py-2">Issued to</th>
              <th className="text-right px-3 py-2">Qty</th>
              <th className="text-left px-3 py-2">By</th>
              <th className="text-center px-3 py-2">Linked</th>
            </tr>
          </thead>
          <tbody>
            {issues.map((it) => (
              <tr key={it.id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`boq-issue-${it.id}`}>
                <td className="px-3 py-2 text-xs text-neutral-500">{fmtDate(it.created_at)}</td>
                <td className="px-3 py-2 capitalize text-neutral-700">{it.type}</td>
                <td className="px-3 py-2 text-neutral-700 text-xs">{it.flat_number || it.site_location || "—"}</td>
                <td className="px-3 py-2 text-neutral-700 text-xs">{it.issued_to || "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmt(it.quantity)} <span className="text-neutral-400 text-[10px]">{it.unit}</span></td>
                <td className="px-3 py-2 text-neutral-500 text-xs">{it.created_by_name || "—"}</td>
                <td className="px-3 py-2 text-center">
                  {it.boq_linked
                    ? <span className="text-[10px] px-1.5 py-0.5 border border-[#CEEAD6] bg-[#E6F4EA] text-[#137333] rounded-sm">Linked</span>
                    : <span className="text-[10px] px-1.5 py-0.5 border border-[#FCE39E] bg-[#FEF7E0] text-[#B06000] rounded-sm">Legacy</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* --- Tab: Materials Required (BOM) --- */
function MaterialsTab({ itemId, composition, totalQty, unit, onSaved }) {
  const [rows, setRows] = useState(() => (composition?.bom || []).map((r, i) => ({ ...r, _key: `${i}-${Math.random()}` })));
  const [saving, setSaving] = useState(false);

  const add = () => setRows((r) => [...r, { _key: String(Math.random()), material: "", unit: "", qty_per_unit: "", rate: "", wastage_pct: 0, notes: "" }]);
  const update = (idx, field, val) => setRows((rs) => rs.map((r, i) => i === idx ? { ...r, [field]: val } : r));
  const remove = (idx) => setRows((rs) => rs.filter((_, i) => i !== idx));

  const rowsWithComputed = rows.map((r) => {
    const qpu = parseFloat(r.qty_per_unit) || 0;
    const w = parseFloat(r.wastage_pct) || 0;
    const rate = parseFloat(r.rate) || 0;
    const effQpu = qpu * (1 + w / 100);
    return { ...r, effQpu, cpu: effQpu * rate, totalQty: effQpu * totalQty, totalCost: effQpu * rate * totalQty };
  });
  const totalCost = rowsWithComputed.reduce((s, r) => s + r.totalCost, 0);

  const save = async () => {
    for (const r of rows) {
      if (!r.material?.trim() || !r.unit?.trim()) { toast.error("Every row needs a material name and unit."); return; }
      if (isNaN(parseFloat(r.qty_per_unit)) || parseFloat(r.qty_per_unit) < 0) { toast.error("Qty per unit must be ≥ 0."); return; }
      if (isNaN(parseFloat(r.rate)) || parseFloat(r.rate) < 0) { toast.error("Rate must be ≥ 0."); return; }
    }
    setSaving(true);
    try {
      const payload = {
        bom: rows.map((r) => ({
          material: r.material.trim(), unit: r.unit.trim(),
          qty_per_unit: parseFloat(r.qty_per_unit) || 0,
          rate: parseFloat(r.rate) || 0,
          wastage_pct: parseFloat(r.wastage_pct) || 0,
          notes: r.notes || "",
        })),
        labour: composition?.labour || [],
        equipment: composition?.equipment || [],
      };
      await api.put(`/boq/items/${itemId}/composition`, payload);
      toast.success("Bill of Materials saved");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="boq-item-tab-content-materials">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="text-[11px] text-neutral-500 max-w-2xl leading-relaxed">
          List every child material this BOQ item consumes per <span className="font-medium text-neutral-800">1 {unit}</span> of finished work. Wastage % is applied on top of qty per unit. Total is scaled by the BOQ quantity ({fmt(totalQty)} {unit}).
        </div>
        <Button onClick={add} className="rounded-sm h-9 gap-1.5 bg-white border border-neutral-300 text-neutral-800 hover:border-primary hover:text-primary" data-testid="materials-add-row">
          <Plus size={12} weight="bold" /> Add material
        </Button>
      </div>
      <div className="border border-neutral-200 bg-white rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="text-left px-3 py-2">Material</th>
              <th className="text-left px-3 py-2 w-20">Unit</th>
              <th className="text-right px-3 py-2 w-28">Qty / {unit}</th>
              <th className="text-right px-3 py-2 w-20">Wastage %</th>
              <th className="text-right px-3 py-2 w-24">Rate</th>
              <th className="text-right px-3 py-2 w-24">Total Qty</th>
              <th className="text-right px-3 py-2 w-28">Cost / unit</th>
              <th className="text-right px-3 py-2 w-28">Total Cost</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {rowsWithComputed.length === 0 && (
              <tr><td colSpan={9} className="p-8 text-center text-neutral-400 text-xs">No materials defined yet. Click <span className="font-medium">Add material</span> to start.</td></tr>
            )}
            {rowsWithComputed.map((r, i) => (
              <tr key={r._key} className="border-t border-neutral-100" data-testid={`materials-row-${i}`}>
                <td className="px-2 py-1.5"><Input value={r.material} onChange={(e) => update(i, "material", e.target.value)} className="h-8 rounded-sm" data-testid={`materials-material-${i}`} /></td>
                <td className="px-2 py-1.5"><Input value={r.unit} onChange={(e) => update(i, "unit", e.target.value)} className="h-8 rounded-sm" placeholder="kg" data-testid={`materials-unit-${i}`} /></td>
                <td className="px-2 py-1.5"><Input type="number" step="0.0001" value={r.qty_per_unit} onChange={(e) => update(i, "qty_per_unit", e.target.value)} className="h-8 rounded-sm text-right tabular-nums" data-testid={`materials-qty-${i}`} /></td>
                <td className="px-2 py-1.5"><Input type="number" step="0.1" value={r.wastage_pct} onChange={(e) => update(i, "wastage_pct", e.target.value)} className="h-8 rounded-sm text-right tabular-nums" data-testid={`materials-wastage-${i}`} /></td>
                <td className="px-2 py-1.5"><Input type="number" step="0.01" value={r.rate} onChange={(e) => update(i, "rate", e.target.value)} className="h-8 rounded-sm text-right tabular-nums" data-testid={`materials-rate-${i}`} /></td>
                <td className="px-2 py-1.5 text-right tabular-nums text-xs text-neutral-500">{fmt(r.totalQty)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-xs text-neutral-500">{fmtINR(r.cpu)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-sm text-neutral-900 font-medium">{fmtINR(r.totalCost)}</td>
                <td className="px-2 py-1.5"><button onClick={() => remove(i)} className="text-neutral-400 hover:text-[#C5221F]" data-testid={`materials-remove-${i}`}><Trash size={13} /></button></td>
              </tr>
            ))}
            {rowsWithComputed.length > 0 && (
              <tr className="border-t-2 border-neutral-200 bg-neutral-50 font-medium">
                <td colSpan={7} className="px-3 py-2 text-right text-xs uppercase tracking-wider text-neutral-500">Grand Material Cost</td>
                <td className="px-3 py-2 text-right tabular-nums text-primary text-lg">{fmtINR(totalCost)}</td>
                <td></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="rounded-sm bg-primary hover:bg-[#0039CC] gap-1.5" data-testid="materials-save">
          <FloppyDisk size={14} /> {saving ? "Saving…" : "Save BOM"}
        </Button>
      </div>
    </div>
  );
}

/* --- Tab: Rate Analysis --- */
function RateAnalysisTab({ itemId, composition, rate, onSaved }) {
  const [labour, setLabour] = useState(() => (composition?.labour || []).map((r, i) => ({ ...r, _key: `L${i}-${Math.random()}` })));
  const [equipment, setEquipment] = useState(() => (composition?.equipment || []).map((r, i) => ({ ...r, _key: `E${i}-${Math.random()}` })));
  const [saving, setSaving] = useState(false);

  const perUnit = rate?.per_unit || { material: 0, labour: 0, equipment: 0, total: 0 };
  const totals = rate?.total || { material: 0, labour: 0, equipment: 0, total: 0 };
  const variance = rate?.variance || { per_unit: 0, total: 0, pct: 0 };

  // Live-recompute labour + equipment as user edits
  const recomputed = useMemoLite(() => {
    const l = labour.reduce((s, r) => s + (parseFloat(r.qty_per_unit) || 0) * (parseFloat(r.rate) || 0), 0);
    const e = equipment.reduce((s, r) => s + (parseFloat(r.qty_per_unit) || 0) * (parseFloat(r.rate) || 0), 0);
    return { labour_per_unit: l, equipment_per_unit: e };
  }, [labour, equipment]);
  const material_per_unit = perUnit.material;
  const draft_total_per_unit = material_per_unit + recomputed.labour_per_unit + recomputed.equipment_per_unit;
  const draft_total = draft_total_per_unit * (rate?.total_qty || 0);

  const save = async () => {
    for (const r of labour) {
      if (!r.trade?.trim()) { toast.error("Every labour row needs a trade."); return; }
    }
    for (const r of equipment) {
      if (!r.name?.trim()) { toast.error("Every equipment row needs a name."); return; }
    }
    setSaving(true);
    try {
      const payload = {
        bom: composition?.bom || [],
        labour: labour.map((r) => ({
          trade: r.trade.trim(),
          qty_per_unit: parseFloat(r.qty_per_unit) || 0,
          unit: r.unit || "manday",
          rate: parseFloat(r.rate) || 0,
          notes: r.notes || "",
        })),
        equipment: equipment.map((r) => ({
          name: r.name.trim(),
          qty_per_unit: parseFloat(r.qty_per_unit) || 0,
          unit: r.unit || "hr",
          rate: parseFloat(r.rate) || 0,
          notes: r.notes || "",
        })),
      };
      await api.put(`/boq/items/${itemId}/composition`, payload);
      toast.success("Rate analysis saved");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const isOver = variance.per_unit > 0;

  return (
    <div className="space-y-6" data-testid="boq-item-tab-content-rate">
      {/* Summary cards */}
      <section>
        <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-3">Cost Breakdown (per {rate?.unit || "unit"})</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <RateCard label="Material" perUnit={material_per_unit} total={totals.material} tone="neutral" />
          <RateCard label="Labour" perUnit={recomputed.labour_per_unit} total={recomputed.labour_per_unit * (rate?.total_qty || 0)} tone="neutral" />
          <RateCard label="Equipment" perUnit={recomputed.equipment_per_unit} total={recomputed.equipment_per_unit * (rate?.total_qty || 0)} tone="neutral" />
          <RateCard label="Analysed Rate" perUnit={draft_total_per_unit} total={draft_total} tone="primary" bold />
        </div>
      </section>

      {/* BOQ rate vs analysed */}
      <section className="border border-neutral-200 rounded-sm p-4 bg-neutral-50">
        <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-2">BOQ vs Analysed</div>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-[10px] text-neutral-500">BOQ Rate</div>
            <div className="tabular-nums text-neutral-900 font-medium">{fmtINR(rate?.boq_rate || 0)} / {rate?.unit}</div>
          </div>
          <div>
            <div className="text-[10px] text-neutral-500">Analysed Rate</div>
            <div className="tabular-nums text-neutral-900 font-medium">{fmtINR(draft_total_per_unit)} / {rate?.unit}</div>
          </div>
          <div>
            <div className="text-[10px] text-neutral-500">Variance</div>
            <div className={`tabular-nums font-medium ${variance.per_unit > 0 ? "text-[#C5221F]" : variance.per_unit < 0 ? "text-[#137333]" : "text-neutral-500"}`}>
              {variance.per_unit === 0 ? "—" : `${variance.per_unit > 0 ? "+" : ""}${fmtINR(variance.per_unit)} (${variance.pct}%)`}
              {isOver && <span className="ml-1 text-[10px]"><Warning size={10} weight="fill" className="inline text-[#C5221F]" /> over BOQ</span>}
            </div>
          </div>
        </div>
      </section>

      {/* Labour editor */}
      <BreakdownEditor
        title="Labour"
        rows={labour}
        setRows={setLabour}
        totalQty={rate?.total_qty || 0}
        fields={[
          { key: "trade", label: "Trade / Role", placeholder: "Mason", testKey: "trade" },
          { key: "unit", label: "Unit", placeholder: "manday", testKey: "unit" },
          { key: "qty_per_unit", label: "Qty/unit", type: "number", step: "0.0001", testKey: "qty" },
          { key: "rate", label: "Rate", type: "number", step: "0.01", testKey: "rate" },
        ]}
        testPrefix="labour"
      />

      {/* Equipment editor */}
      <BreakdownEditor
        title="Equipment / Plant"
        rows={equipment}
        setRows={setEquipment}
        totalQty={rate?.total_qty || 0}
        fields={[
          { key: "name", label: "Equipment", placeholder: "Concrete Mixer", testKey: "name" },
          { key: "unit", label: "Unit", placeholder: "hr", testKey: "unit" },
          { key: "qty_per_unit", label: "Qty/unit", type: "number", step: "0.0001", testKey: "qty" },
          { key: "rate", label: "Rate", type: "number", step: "0.01", testKey: "rate" },
        ]}
        testPrefix="equipment"
      />

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="rounded-sm bg-primary hover:bg-[#0039CC] gap-1.5" data-testid="rate-save">
          <FloppyDisk size={14} /> {saving ? "Saving…" : "Save Rate Analysis"}
        </Button>
      </div>
    </div>
  );
}

function useMemoLite(fn, deps) {
  const [val, setVal] = useState(fn());
  useEffect(() => { setVal(fn()); /* eslint-disable-next-line */ }, deps);
  return val;
}

function RateCard({ label, perUnit, total, tone = "neutral", bold = false }) {
  const tint = tone === "primary" ? "text-primary border-primary/30 bg-primary/5" : "text-neutral-900 border-neutral-200 bg-white";
  return (
    <div className={`border rounded-sm p-3.5 ${tint}`}>
      <div className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
      <div className={`font-display ${bold ? "text-2xl" : "text-xl"} font-bold tabular-nums mt-1`}>{fmtINR(perUnit)}</div>
      <div className="text-[10px] text-neutral-400 tabular-nums mt-0.5">Total: {fmtINR(total)}</div>
    </div>
  );
}

function BreakdownEditor({ title, rows, setRows, totalQty, fields, testPrefix }) {
  const add = () => setRows((r) => [...r, { _key: String(Math.random()), ...Object.fromEntries(fields.map(f => [f.key, ""])), notes: "" }]);
  const update = (idx, field, val) => setRows((rs) => rs.map((r, i) => i === idx ? { ...r, [field]: val } : r));
  const remove = (idx) => setRows((rs) => rs.filter((_, i) => i !== idx));
  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wider text-neutral-500">{title}</div>
        <Button onClick={add} className="rounded-sm h-8 gap-1.5 bg-white border border-neutral-300 text-neutral-800 hover:border-primary hover:text-primary text-xs" data-testid={`${testPrefix}-add`}>
          <Plus size={11} weight="bold" /> Add row
        </Button>
      </div>
      <div className="border border-neutral-200 bg-white rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500">
            <tr>
              {fields.map((f) => <th key={f.key} className="text-left px-3 py-2">{f.label}</th>)}
              <th className="text-right px-3 py-2 w-28">Cost/unit</th>
              <th className="text-right px-3 py-2 w-28">Total</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={fields.length + 3} className="p-6 text-center text-neutral-400 text-xs">No {title.toLowerCase()} rows yet.</td></tr>
            )}
            {rows.map((r, i) => {
              const qpu = parseFloat(r.qty_per_unit) || 0;
              const rt = parseFloat(r.rate) || 0;
              const cpu = qpu * rt;
              return (
                <tr key={r._key} className="border-t border-neutral-100" data-testid={`${testPrefix}-row-${i}`}>
                  {fields.map((f) => (
                    <td key={f.key} className="px-2 py-1.5">
                      <Input
                        type={f.type || "text"}
                        step={f.step}
                        placeholder={f.placeholder}
                        value={r[f.key] ?? ""}
                        onChange={(e) => update(i, f.key, e.target.value)}
                        className={`h-8 rounded-sm ${f.type === "number" ? "text-right tabular-nums" : ""}`}
                        data-testid={`${testPrefix}-${f.testKey}-${i}`}
                      />
                    </td>
                  ))}
                  <td className="px-2 py-1.5 text-right tabular-nums text-xs text-neutral-500">{fmtINR(cpu)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-sm text-neutral-900">{fmtINR(cpu * totalQty)}</td>
                  <td className="px-2 py-1.5"><button onClick={() => remove(i)} className="text-neutral-400 hover:text-[#C5221F]" data-testid={`${testPrefix}-remove-${i}`}><Trash size={13} /></button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* --- Tab: Timeline --- */
const ACTION_META = {
  "po.created": { color: "text-[#1A73E8]", bg: "bg-[#E8F0FE]", label: "PO raised" },
  "po.status_changed": { color: "text-[#137333]", bg: "bg-[#E6F4EA]", label: "PO status changed" },
  "po.deleted": { color: "text-[#C5221F]", bg: "bg-[#FCE8E6]", label: "PO deleted" },
  "invoice.uploaded": { color: "text-[#1A73E8]", bg: "bg-[#E8F0FE]", label: "Invoice uploaded" },
  "invoice.approved": { color: "text-[#137333]", bg: "bg-[#E6F4EA]", label: "Invoice approved" },
  "invoice.rejected": { color: "text-[#C5221F]", bg: "bg-[#FCE8E6]", label: "Invoice rejected" },
  "invoice.re_verified": { color: "text-[#B06000]", bg: "bg-[#FEF7E0]", label: "Invoice re-verified" },
  "invoice.deleted": { color: "text-[#C5221F]", bg: "bg-[#FCE8E6]", label: "Invoice deleted" },
  "boq.created": { color: "text-[#137333]", bg: "bg-[#E6F4EA]", label: "BOQ item created" },
  "boq.node_updated": { color: "text-[#B06000]", bg: "bg-[#FEF7E0]", label: "BOQ updated" },
  "boq.node_moved": { color: "text-[#B06000]", bg: "bg-[#FEF7E0]", label: "BOQ moved" },
  "boq.deleted": { color: "text-[#C5221F]", bg: "bg-[#FCE8E6]", label: "BOQ deleted" },
  "budget_revision.created": { color: "text-[#7627CD]", bg: "bg-[#F3E8FD]", label: "Budget revision requested" },
  "budget_revision.approved": { color: "text-[#137333]", bg: "bg-[#E6F4EA]", label: "Budget revision approved" },
  "budget_revision.rejected": { color: "text-[#C5221F]", bg: "bg-[#FCE8E6]", label: "Budget revision rejected" },
};

function TimelineTab({ entries }) {
  if (!entries.length) {
    return (
      <div className="border border-dashed border-neutral-200 rounded-sm p-12 text-center" data-testid="boq-item-tab-content-timeline">
        <Clock size={36} className="mx-auto text-neutral-300 mb-3" />
        <div className="text-sm text-neutral-500">No activity yet on this item.</div>
      </div>
    );
  }
  return (
    <div className="relative pl-8" data-testid="boq-item-tab-content-timeline">
      <div className="absolute left-3 top-0 bottom-0 w-px bg-neutral-200" />
      <div className="space-y-4">
        {entries.map((e) => {
          const meta = ACTION_META[e.action] || { color: "text-neutral-700", bg: "bg-neutral-100", label: (e.action || "").replace(/[._]/g, " ") };
          return (
            <div key={e.id} className="relative" data-testid={`boq-timeline-${e.id}`}>
              <div className={`absolute -left-8 top-1 w-6 h-6 rounded-full border-2 border-white flex items-center justify-center ${meta.bg}`}>
                <CheckCircle size={12} weight="fill" className={meta.color} />
              </div>
              <div className="border border-neutral-200 bg-white rounded-sm p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className={`text-xs font-medium ${meta.color}`}>{meta.label}</div>
                    {e.entity_label && (
                      <div className="text-sm text-neutral-800 mt-0.5 flex items-center gap-1">
                        {e.entity_type === "po" && <FileText size={12} className="text-neutral-400" />}
                        {e.entity_label}
                      </div>
                    )}
                    {e.metadata && Object.keys(e.metadata).length > 0 && (
                      <div className="text-[11px] text-neutral-500 mt-1 flex flex-wrap gap-x-2 gap-y-0.5">
                        {Object.entries(e.metadata).slice(0, 4).map(([k, v]) => (
                          <span key={k}><span className="text-neutral-400">{k}:</span> <span className="text-neutral-700">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span></span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-[10px] text-neutral-400 tabular-nums whitespace-nowrap">{fmtDate(e.timestamp)}</div>
                </div>
                <div className="text-[10px] text-neutral-400 mt-1">by {e.actor_name || "system"}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
